import { motion } from "framer-motion";
import { Activity, Cpu, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const steps = [
  {
    icon: Activity,
    step: "01",
    title: "Audit Semantic AI",
    subtitle: "Descoperim ce nu vede AI-ul",
    description:
      "Scanăm site-ul tău pentru erori de indexare și identificăm lipsa contextului semantic pe care ChatGPT și Perplexity îl necesită pentru a-ți recomanda afacerea.",
    features: [
      "Detectare erori meta-date și Schema.org",
      "Analiză compatibilitate cu LLM-uri",
      "Raport detaliat de vizibilitate AI",
      "Identificare oportunități GEO",
    ],
  },
  {
    icon: Cpu,
    step: "02",
    title: "Motorul de Generative Engine Optimization",
    subtitle: "Transformăm textul în date inteligente",
    description:
      "Convertim automat conținutul tău în date structurate JSON-LD pe care motoarele AI le pot citi, înțelege și recomanda utilizatorilor.",
    features: [
      "Generare automată JSON-LD",
      "Markup semantic pentru produse",
      "Optimizare pentru citări AI",
      "Structurare FAQ și How-To",
    ],
    hasCodePreview: true,
  },
  {
    icon: Zap,
    step: "03",
    title: "Real-Time Trend Sync",
    subtitle: "Sincronizare cu piața din România",
    description:
      "Ne conectăm la Google Trends API și actualizăm automat paginile tale pentru a reflecta căutările momentului din România, ținându-te mereu relevant.",
    features: [
      "Integrare Google Trends API",
      "Actualizare automată conținut",
      "Alerte trenduri în timp real",
      "Adaptare sezonieră RO",
    ],
  },
];

const stats = [
  { value: "500+", label: "Site-uri Optimizate" },
  { value: "2M+", label: "Pagini Procesate" },
  { value: "89%", label: "Creștere Medie Trafic" },
  { value: "24/7", label: "Monitorizare Activă" },
];

function CodePreview() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="mt-6 relative"
    >
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 rounded-xl blur-lg opacity-75" />
      
      <div className="relative bg-slate-900 rounded-xl p-4 font-mono text-xs overflow-hidden">
        {/* Code header */}
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-700">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="ml-2 text-slate-500 text-[10px]">schema.json</span>
        </div>
        
        {/* Code content with glow */}
        <div className="space-y-1 text-slate-300">
          <div>
            <span className="text-purple-400">{"{"}</span>
          </div>
          <div className="pl-4">
            <span className="text-emerald-400">"@context"</span>
            <span className="text-slate-500">: </span>
            <span className="text-amber-300">"https://schema.org"</span>
            <span className="text-slate-500">,</span>
          </div>
          <div className="pl-4">
            <span className="text-emerald-400">"@type"</span>
            <span className="text-slate-500">: </span>
            <span className="text-amber-300">"Product"</span>
            <span className="text-slate-500">,</span>
          </div>
          <div className="pl-4">
            <span className="text-emerald-400">"name"</span>
            <span className="text-slate-500">: </span>
            <span className="text-amber-300">"Produs Optimizat"</span>
            <span className="text-slate-500">,</span>
          </div>
          <div className="pl-4 flex items-center gap-2">
            <span className="text-emerald-400">"aggregateRating"</span>
            <span className="text-slate-500">: </span>
            <span className="text-purple-400">{"{"}</span>
            <span className="animate-pulse text-primary">█</span>
          </div>
          <div>
            <span className="text-purple-400">{"}"}</span>
          </div>
        </div>
        
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" />
      </div>
    </motion.div>
  );
}

export function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent text-accent-foreground text-sm font-medium mb-6">
            <Cpu className="w-4 h-4" />
            Tehnologie Avansată
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
            Cum funcționează OptiRO:{" "}
            <span className="text-gradient">
              Puntea dintre Magazinul tău și Inteligența Artificială
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Automatizăm vizibilitatea pe Google și în recomandările AI prin 3
            pași tehnologici avansați.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-16 mb-20">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`flex flex-col ${
                index % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
              } gap-8 lg:gap-16 items-center`}
            >
              {/* Content */}
              <div className="flex-1 w-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <step.icon className="w-7 h-7 text-primary" />
                  </div>
                  <span className="text-5xl font-bold text-muted-foreground/20">
                    {step.step}
                  </span>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-primary font-medium mb-4">{step.subtitle}</p>
                <p className="text-muted-foreground mb-6 text-lg">
                  {step.description}
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {step.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-foreground"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {step.hasCodePreview && <CodePreview />}
              </div>

              {/* Visual Card */}
              <div className="flex-1 w-full max-w-md">
                <div className="card-elevated p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl" />
                  <div className="relative">
                    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-6">
                      <step.icon className="w-10 h-10 text-primary" />
                    </div>
                    <div className="space-y-3">
                      {step.features.slice(0, 3).map((feature, i) => (
                        <div
                          key={feature}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                          style={{ opacity: 1 - i * 0.15 }}
                        >
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">
                              {i + 1}
                            </span>
                          </div>
                          <span className="text-sm text-foreground">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20 py-12 border-y border-border"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-primary/5 via-accent/50 to-primary/5 rounded-3xl p-6 sm:p-12 border border-primary/10 overflow-hidden">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Ești gata să domini căutările AI?
            </h3>
            <p className="text-muted-foreground mb-8 text-lg">
              Află în 30 de secunde dacă site-ul tău este pregătit pentru era
              inteligenței artificiale.
            </p>
            <Button
              variant="hero"
              size="xl"
              className="group w-full sm:w-auto"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Scanează site-ul gratuit acum
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
